/**
 * Cascade-delete of a message's dependent local rows (issue #978).
 *
 * When the sync engine (or any caller) removes a message from `db.messages`,
 * several other chat-scoped stores may still hold rows that reference that
 * message. Left behind they become ORPHANS that no live message points at:
 *   - `pinnedMessages`  — a pinned card for a now-deleted message
 *   - `mediaAttachments` / `fileAttachments` / `voiceAttachments` — cached
 *     attachment rows (+ their OPFS blobs)
 *   - `links`           — the link-preview row
 *   - the parent thread message's denormalized `commentCount`
 *
 * ── Deletion model (verified against db.ts + the write paths) ───────────────
 * `db.messages` primary key is `_clientId` (first field of the v8+ schema
 * string); `id` is the SERVER id (a secondary index). A single message row
 * therefore carries TWO identifiers we must reconcile against, because the
 * dependent stores were written under different conventions:
 *
 *  • pinnedMessages — PK = message id. Written under the SERVER id (pin flow).
 *  • links          — PK = message id, but the OPTIMISTIC send path writes it
 *                     under the CLIENT id (`_clientId`) and the WS/sync path
 *                     under the SERVER id. So a link can live under EITHER key.
 *  • attachments    — PK = the attachment's OWN id (the content-storage id),
 *                     with a NON-indexed `messageId` FK field + an indexed
 *                     `chatId`. The `messageId` FK is populated INCONSISTENTLY:
 *                     the primary send path and the share/re-encrypt path store
 *                     `_clientId`, while the edit/replace path stores the server
 *                     `id`. Content may also live in OPFS (`contentStoredIn`).
 *  • commentCount   — a denormalized counter on the PARENT (thread-root)
 *                     message. A comment row has `threadId` = the parent's id.
 *
 * ── Cascade strategy (over-delete-safe) ─────────────────────────────────────
 * We identify dependents using ONLY identifiers that uniquely belong to the
 * deleted message, so the sweep can never touch an unrelated message's rows:
 *
 *  1. pinnedMessages + links: bulk-delete the message-id keys
 *     {existing.id, existing._clientId}. Both are unique identifiers of THIS
 *     message (server ids and client UUID7s never collide), so deleting these
 *     keys cannot remove another message's row. A non-existent key is a no-op.
 *  2. attachments: sweep each table by `messageId ∈ {existing.id,
 *     existing._clientId}` (scoped through the `chatId` index for efficiency)
 *     AND target the attachment ids referenced by this message's own
 *     `content.{media,file,voice}.attachmentId`. Both signals are scoped to
 *     THIS message; a different message carries a different messageId / content.
 *  3. commentCount: if the deleted row IS a comment (`threadId` set), decrement
 *     the parent's counter by one, floored at 0. We DECREMENT rather than
 *     recompute from local rows because not all comments are necessarily cached
 *     locally — recomputing could wrongly drop the count. The server's absolute
 *     count (via `applyThreadUpdates`) still overwrites/heals any drift on the
 *     next sync.
 *
 * ── Atomicity & best-effort contract ────────────────────────────────────────
 * The Dexie-table writes run inside a single `rw` transaction so the sweep is
 * all-or-nothing. OPFS blob deletion happens AFTER the transaction commits
 * (OPFS is not part of the IndexedDB transaction). The whole function is
 * best-effort: it NEVER throws — a cascade failure must not break or roll back
 * the source-of-truth `db.messages` delete that the caller already performed
 * (and, in the sync engine's strict path, must not hold the sync cursor).
 *
 * NOTE: deliberately OUT of scope — deleting a thread's CHILD comments when the
 * thread ROOT is deleted. That is a separate concern (potential large fan-out /
 * different over-delete surface) tracked by the existing TO DO in
 * messageEventsHandler; this cascade only cleans the deleted message's OWN
 * dependents and adjusts the parent counter when a comment is removed.
 */

import type { EntityTable } from "dexie";
import { db } from "@/indexedDB/db";
import type {
  MessageEntity,
  MediaAttachmentEntity,
  FileAttachmentEntity,
  VoiceAttachmentEntity,
} from "@/indexedDB/types";
import { createLogger } from "@/utils/debug";

const log = createLogger("SyncCascadeDelete");

// Lazy import: `@/lib/storage` pulls the OPFS layer + environment detection
// (`window.matchMedia`) into the module graph. The sync engine statically
// imports this cascade, so keep that heavy, browser-only code OUT of the static
// graph (mirrors syncEngine's lazy-import discipline) — it is only needed for
// the post-transaction OPFS blob sweep. Imported ONCE per cascade (not per id).
const lazyStorage = () => import("@/lib/storage");

type AnyAttachmentEntity =
  | MediaAttachmentEntity
  | FileAttachmentEntity
  | VoiceAttachmentEntity;

/**
 * Collect the attachment PKs owned by `existing` in a single attachment table.
 *
 * Union of two message-scoped signals:
 *   - rows whose `messageId` FK matches one of `messageIdKeys` (scoped by the
 *     `chatId` index), and
 *   - the attachment ids referenced by the message's own content metadata that
 *     actually exist as a row in THIS table.
 *
 * Both signals are unique to `existing`, so the result can never include an
 * unrelated message's attachment.
 */
async function collectAttachmentIds(
  table: EntityTable<AnyAttachmentEntity, "id">,
  chatId: string,
  messageIdKeys: Set<string>,
  contentAttachmentIds: Set<string>,
): Promise<Set<string>> {
  const ids = new Set<string>();

  // Signal 1: FK sweep, scoped through the chatId index.
  const rows = await table.where("chatId").equals(chatId).toArray();
  for (const row of rows) {
    if (row.messageId && messageIdKeys.has(row.messageId)) {
      ids.add(row.id);
    }
    // Signal 2 (verified against this table): the content id resolves to a real
    // row here. Checking the already-fetched rows avoids extra `.get()` calls
    // and guarantees we only ever delete an id that belongs to THIS chat.
    if (contentAttachmentIds.has(row.id)) {
      ids.add(row.id);
    }
  }

  return ids;
}

/**
 * Best-effort cascade: remove every local dependent of an already-deleted
 * message. NEVER throws.
 *
 * @param existing The message row as read from Dexie BEFORE it was deleted
 *                 (the caller must capture it before removing the row, because
 *                 the attachment content ids live on `existing.content`).
 */
export async function cascadeDeleteMessageDependents(
  existing: MessageEntity,
): Promise<void> {
  try {
    const { chatId } = existing;

    // Message-id keys that could key a pinned/link row or an attachment FK.
    const messageIdKeys = new Set<string>(
      [existing.id, existing._clientId].filter(
        (k): k is string => typeof k === "string" && k.length > 0,
      ),
    );

    // Attachment ids referenced by this message's own content metadata, kept
    // PER-TYPE (media / file / voice) rather than merged into one set. Dexie
    // enforces PK uniqueness only WITHIN a store, not ACROSS the media/file/voice
    // stores, so a single merged set applied to every table could — on a
    // cross-store id collision — match and delete an UNRELATED message's
    // attachment of a different type (its OPFS blob too). Scoping each content id
    // to its own table removes that over-delete surface entirely. The FK sweep is
    // already per-table, so this narrowing can never miss THIS message's own
    // attachments. (Cross-model review.)
    const asIdSet = (id: string | undefined): Set<string> =>
      typeof id === "string" && id.length > 0 ? new Set([id]) : new Set();
    const mediaContentIds = asIdSet(existing.content?.media?.attachmentId);
    const fileContentIds = asIdSet(existing.content?.file?.attachmentId);
    const voiceContentIds = asIdSet(existing.content?.voice?.attachmentId);

    // Parent thread id, if this row is a comment (drives the count decrement).
    const parentThreadId =
      typeof existing.threadId === "string" && existing.threadId.length > 0
        ? existing.threadId
        : null;

    // Collected attachment PKs (per table) so OPFS blobs can be swept after the
    // transaction commits.
    const mediaIds = new Set<string>();
    const fileIds = new Set<string>();
    const voiceIds = new Set<string>();

    await db.transaction(
      "rw",
      [
        db.messages,
        db.chats,
        db.pinnedMessages,
        db.links,
        db.mediaAttachments,
        db.fileAttachments,
        db.voiceAttachments,
      ],
      async () => {
        // --- pinned + link rows (keyed by a message id) ---
        const keys = [...messageIdKeys];
        await db.pinnedMessages.bulkDelete(keys);
        await db.links.bulkDelete(keys);
        // Keep the chat's authoritative pinned-id set in sync so a later
        // reconcile does not re-fetch the deleted message as pinned.
        await db.chats
          .where("id")
          .equals(chatId)
          .modify((chat) => {
            if (chat.pinnedMessageIds?.length) {
              const next = chat.pinnedMessageIds.filter(
                (pid) => !messageIdKeys.has(pid),
              );
              if (next.length !== chat.pinnedMessageIds.length) {
                chat.pinnedMessageIds = next;
              }
            }
          });

        // --- attachment rows (FK sweep + content-id targets) ---
        for (const id of await collectAttachmentIds(
          db.mediaAttachments,
          chatId,
          messageIdKeys,
          mediaContentIds,
        )) {
          mediaIds.add(id);
        }
        for (const id of await collectAttachmentIds(
          db.fileAttachments,
          chatId,
          messageIdKeys,
          fileContentIds,
        )) {
          fileIds.add(id);
        }
        for (const id of await collectAttachmentIds(
          db.voiceAttachments,
          chatId,
          messageIdKeys,
          voiceContentIds,
        )) {
          voiceIds.add(id);
        }

        if (mediaIds.size) {
          await db.mediaAttachments.bulkDelete([...mediaIds]);
        }
        if (fileIds.size) {
          await db.fileAttachments.bulkDelete([...fileIds]);
        }
        if (voiceIds.size) {
          await db.voiceAttachments.bulkDelete([...voiceIds]);
        }

        // --- parent thread comment-count (decrement, floored at 0) ---
        // Resolve a SINGLE parent row by its primary key (`_clientId`) before
        // writing. `id` is a NON-unique secondary index (messages PK is
        // `_clientId`; see db.ts), so `.where("id")...modify(...)` would run the
        // decrement once PER matching row and over-decrement if a duplicate-id
        // row ever exists. We compute the next count once and update only that
        // one resolved row — matching how the rest of the sync engine looks up
        // messages by server id (`.where("id").equals(...).first()`).
        if (parentThreadId) {
          const parent = await db.messages
            .where("id")
            .equals(parentThreadId)
            .first();
          if (parent) {
            const current =
              typeof parent.commentCount === "number" ? parent.commentCount : 0;
            const next = Math.max(0, current - 1);
            if (next !== current) {
              await db.messages.update(parent._clientId, {
                commentCount: next,
              });
            }
          }
        }
      },
    );

    // --- OPFS blob cleanup (outside the IndexedDB transaction) ---
    // Resolve the storage module once, then sweep each blob independently so one
    // failure can't skip the rest.
    const opfsIds = [...mediaIds, ...fileIds, ...voiceIds];
    if (opfsIds.length) {
      const { deleteAttachmentContent } = await lazyStorage();
      await Promise.all(
        opfsIds.map(async (id) => {
          try {
            await deleteAttachmentContent(id);
          } catch (err) {
            log.warn("[cascade] OPFS content delete failed", { id, err });
          }
        }),
      );
    }
  } catch (err) {
    // Best-effort: a cascade failure must not surface to the caller (it would
    // otherwise risk rolling back / blocking the source-of-truth message delete
    // or holding the sync cursor in the strict event-apply path).
    log.warn("[cascade] cascadeDeleteMessageDependents failed", {
      messageId: existing?.id,
      err,
    });
  }
}
